import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CollapsibleSection from '../CollapsibleSection';
import MessageModal from '../MessageModal';

const InvoicesSection = ({ invoices = [], loading = false }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState(null);

  const handleSendReminder = (invoice) => {
    // Set up email modal data for invoice_issued template
    setEmailModalData({
      templateType: 'invoice_issued',
      recipientEmail: invoice.clientEmail || '',
      contextData: {
        client_name: invoice.clientName,
        project_name: invoice.projectName,
        invoice_number: invoice.invoiceNumber || '',
        invoice_total: `$${invoice.amount?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}`,
        due_date: invoice.dueDate || '',
        invoice_link: `${window.location.origin}/invoices/edit/${invoice.id}`
      },
      quoteId: null,
      opportunityId: null
    });
    setShowEmailModal(true);
  };

  const handleViewInvoice = (invoice) => {
    // Navigate to invoice details page
    window.location.href = `/invoices/edit/${invoice.id}`;
  };

  const InvoiceCard = ({ invoice }) => {
    const isOverdue = invoice.status === 'overdue';
    const statusColor = isOverdue ? 'red' : 'yellow';
    const statusBgColor = isOverdue ? 'bg-red-100' : 'bg-yellow-100';
    const statusTextColor = isOverdue ? 'text-red-800' : 'text-yellow-800';

    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-gray-900 truncate">{invoice.clientName}</h4>
              <span className={`${statusBgColor} ${statusTextColor} text-xs font-medium px-2 py-0.5 rounded`}>
                {isOverdue ? 'Overdue' : 'Upcoming'}
              </span>
            </div>
            <p className="text-sm text-gray-600 truncate">{invoice.projectName}</p>
          </div>
          <span className={`text-lg font-bold ${isOverdue ? 'text-red-600' : 'text-gray-900'} flex-shrink-0`}>
            ${invoice.amount?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
            {isOverdue
              ? `${invoice.daysOverdue}d overdue`
              : `Due in ${invoice.dueIn}d`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => isOverdue ? handleSendReminder(invoice) : handleViewInvoice(invoice)}
              className="text-xs text-blue-600 hover:text-blue-900 font-medium"
            >
              {isOverdue ? 'Send reminder' : 'View'}
            </button>
            <Link
              to={`/invoices/edit/${invoice.id}`}
              className="text-xs text-gray-600 hover:text-gray-900 font-medium"
            >
              Details
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const loadingCards = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex justify-between mb-2">
            <div className="h-5 bg-gray-200 rounded w-1/2"></div>
            <div className="h-5 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="text-center py-8">
      <p className="text-gray-500">No upcoming or overdue invoices</p>
    </div>
  );

  // Sort invoices: overdue first, then by due date
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (a.status !== 'overdue' && b.status === 'overdue') return 1;
    return 0;
  });

  const invoicesList = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {sortedInvoices.map((invoice) => (
        <InvoiceCard key={invoice.id} invoice={invoice} />
      ))}
    </div>
  );

  const overdueCount = invoices.filter(inv => inv.status === 'overdue').length;

  return (
    <>
      <CollapsibleSection
        title="Invoices"
        icon={
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        }
        badge={loading ? '...' : `${invoices.length}${overdueCount > 0 ? ` (${overdueCount} overdue)` : ''}`}
        defaultOpen={true}
      >
        {loading ? loadingCards : invoices.length === 0 ? emptyState : invoicesList}
      </CollapsibleSection>

      {/* Email Modal for sending invoice reminders */}
      {showEmailModal && emailModalData && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEmailModalData(null);
          }}
          templateType={emailModalData.templateType}
          recipientEmail={emailModalData.recipientEmail}
          contextData={emailModalData.contextData}
          onSuccess={() => {
            setShowEmailModal(false);
            setEmailModalData(null);
          }}
        />
      )}
    </>
  );
};

export default InvoicesSection;
